import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, filter, map, of, takeUntil } from "rxjs";
import { AppConfigService, LoggingService, SessionService } from "src/app/core";
import { EventContextService } from "src/app/features/events";
import { VisionboardItemSearchCommands } from "../../commands";
import { VisionboardItem } from "../../models";
import { VbGlobalSearchResponse, VisionboardItemSearchResponses } from "../../responses";
import {
  VisionboardAttributesService,
  VisionboardCategoriesService,
  VisionboardVersionsService,
  VisionboardWorkspaceItemsStoreService,
} from "../../services";
import { SearchItemResult, VisionboardItemSearchService } from "../../services/visionboard-item-search.service";

@Injectable()
export class VisionboardGlobalItemSearchService extends VisionboardItemSearchService {
  private _subCategoryIds: number[];

  private _searchTerm$: BehaviorSubject<string>;
  searchTerm$: Observable<string>;

  private _subCategorySelect$: Subject<[string, string]>;
  subCategorySelect$: Observable<[string, string]>;

  private _itemSelect$: Subject<VisionboardItem>;
  itemSelect$: Observable<VisionboardItem>;

  constructor(
    protected visionboardAttributesService: VisionboardAttributesService,
    protected visionboardVersionsService: VisionboardVersionsService,
    protected visionboardCategoriesService: VisionboardCategoriesService,
    protected visionboardWorkspaceItemsStoreService: VisionboardWorkspaceItemsStoreService,
    protected appConfigService: AppConfigService,
    protected http: HttpClient,
    protected eventContextService: EventContextService,
    protected sessionService: SessionService,
  ) {
    super(
      visionboardAttributesService,
      visionboardVersionsService,
      visionboardCategoriesService,
      visionboardWorkspaceItemsStoreService,
      appConfigService,
      http,
      eventContextService,
      sessionService,
    );
  }

  search(searchTerm: string): void {
    this._searchTerm$.next(searchTerm);
  }

  getSearchTerm(): string {
    return this._searchTerm$.value;
  }

  subCategorySelect(subcategory: [string, string]): void {
    this._subCategorySelect$.next(subcategory);
  }

  itemSelect(item: VisionboardItem): void {
    this._itemSelect$.next(item);
  }

  setSubCategoryIds(subCategoryIds: number[]): void {
    this._subCategoryIds = subCategoryIds;
  }

  getSubCategoryIds(): number[] {
    return this._subCategoryIds;
  }

  getPageSize(applyOffset: boolean = true): number {
    return 24;
  }

  showCustomRequestEmptyState(): boolean {
    return this.sessionService.canMakeCustomRequests() && !this.eventContextService.isGuestEvent();
  }

  showCustomUploadSearchResult(): boolean {
    return false;
  }

  protected init(): void {
    super.init();

    this._searchTerm$ = new BehaviorSubject<string>(null);
    this.searchTerm$ = this._searchTerm$.asObservable();

    this._subCategorySelect$ = new Subject<[string, string]>();
    this.subCategorySelect$ = this._subCategorySelect$.asObservable();

    this._itemSelect$ = new Subject<VisionboardItem>();
    this.itemSelect$ = this._itemSelect$.asObservable();
  }

  protected subscribe(): void {
    this.updateItemsSub();

    this.searchTerm$
      .pipe(
        filter(searchTerm => !!searchTerm),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => {
          this.updateItems$.next({ page: 1, reset: true, includeTotalCount: true });
        },
      });
  }

  protected getSearchApiUrl(): string {
    return this.appConfigService.getVisionboardGlobalSearchApiUrl(this.visionboardVersionsService.getEventId());
  }

  protected getVisionboardItems$(
    count: number,
    page: number,
    includeTotalCount: boolean,
    skipVersionCheck?: boolean,
  ): Observable<SearchItemResult> {
    const version = this.visionboardVersionsService.getSelectedVersion();

    if ((version || skipVersionCheck) && this.getSearchTerm()) {
      return this.http
        .post<VbGlobalSearchResponse>(
          this.getSearchApiUrl(),
          VisionboardItemSearchCommands.getVbGlobalSearchCommand(
            count,
            page,
            includeTotalCount,
            this.eventContextService.getContextType(),
            this.getSearchTerm(),
          ),
        )
        .pipe(
          map(response => {
            const favListByItems = this.visionboardWorkspaceItemsStoreService.getFavoriteListIdsBySkuId();
            const items = VisionboardItemSearchResponses.getVisionboardSearchItems(response, id => favListByItems.get(id));

            return {
              items: items[0],
              count: items[1],
            };
          }),
        );
    }

    LoggingService.log("VisionboardItemSearchService - version/category/attributes missing, fetching item skipped");
    return of(null);
  }
}
